import { Link } from 'react-router-dom';
import { useAuth } from '../../../lib/auth-context.js';
import { useDashboardSummary } from '../../dashboard/hooks.js';

/**
 * Setup wizard. Three checkpoints, each pulling state from a real
 * source so the user knows where they are in the rollout. Each
 * card "deep-links" the user into the page where the next step
 * happens.
 *
 * Step 1 — auth (always green if the user got past `<RequireAuth>`).
 * Step 2 — engine reachability (the dashboard summary call hits the
 *          engine end-to-end; success means the engine JWT mint +
 *          OAuth credentials are correctly populated).
 * Step 3 — Workfront object catalog non-empty (the engine has
 *          discovered objects in the customer's Workfront tenant).
 */
export function SetupWizardPage(): JSX.Element {
  const { session } = useAuth();
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useDashboardSummary();

  const step1Done = !!session;
  const step2Done = !!summary && !summaryError;
  const step3Done = !!summary && (summary.orgsCount ?? 0) > 0;

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Setup</h1>
        <p className="text-sm text-foreground/60">
          Three-step onboarding — status reflects live system state.
        </p>
      </header>
      <ol className="space-y-3 px-6 py-4">
        <Step
          number={1}
          title="Sign in"
          description="Authenticate against your Workfront tenant."
          state={step1Done ? 'done' : 'pending'}
          cta={step1Done ? null : { to: '/', label: 'Sign in' }}
        />
        <Step
          number={2}
          title="Connect the engine"
          description="The engine fetches your Fusion organizations + scenarios. Status comes from /v1/dashboard/summary."
          state={
            summaryLoading ? 'checking' : step2Done ? 'done' : summaryError ? 'failed' : 'pending'
          }
          cta={step2Done ? { to: '/dashboard', label: 'View dashboard' } : null}
        />
        <Step
          number={3}
          title="Discover Workfront objects"
          description="Once the engine talks to Workfront, the object catalog populates."
          state={step3Done ? 'done' : 'pending'}
          cta={step3Done ? { to: '/workfront', label: 'View catalog' } : null}
        />
      </ol>
    </div>
  );
}

function Step({
  number,
  title,
  description,
  state,
  cta,
}: {
  number: number;
  title: string;
  description: string;
  state: 'pending' | 'checking' | 'done' | 'failed';
  cta: { to: string; label: string } | null;
}): JSX.Element {
  const dotClass =
    state === 'done'
      ? 'bg-emerald-400'
      : state === 'failed'
        ? 'bg-rose-400'
        : state === 'checking'
          ? 'bg-amber-400 animate-pulse'
          : 'bg-foreground/20';
  return (
    <li className="flex gap-3 rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
      <span
        className={`mt-1.5 inline-block h-3 w-3 shrink-0 rounded-full ${dotClass}`}
        aria-hidden="true"
      />
      <div className="grow">
        <div className="text-sm font-medium text-foreground">
          Step {number}: {title}
        </div>
        <p className="text-xs text-foreground/60">{description}</p>
      </div>
      {cta ? (
        <Link
          to={cta.to}
          className="self-center rounded-md border border-foreground/15 bg-foreground/5 px-3 py-1.5 text-xs text-foreground/80 transition-colors hover:border-foreground/40 hover:text-foreground"
        >
          {cta.label}
        </Link>
      ) : null}
    </li>
  );
}
